<template>
  <div>
    <h4>Set Allowed Sellers</h4>

    <CForm
      novalidate
      @submit.prevent
    >
      <CRow>
        <CCol
          :md="{ size: '12' }"
          :sm="{ size: '12' }"
        >
          <CAlert
            v-if="allowedSellersSource === 'buyer'"
            color="info"
            class="mb-3"
          >
            <strong>Inherited from Buyer Level:</strong> This list is currently inherited from the buyer account settings.
            <router-link
              v-if="buyerAccountId"
              :to="{ name: 'BuyerAccountDetails', params: { id: buyerAccountId }, hash: '#section-traffic' }"
              target="_blank"
            >
              View in Buyer Account
            </router-link>
            <br>
            You can override it by making changes and saving.
          </CAlert>

          <CAlert
            v-if="allowedSellersSource === 'campaign'"
            color="success"
            class="mb-3"
          >
            <strong>Campaign Level Override:</strong> This list has been customized at the campaign level and overrides the buyer account settings, if set.
            <router-link
              v-if="buyerAccountId"
              :to="{ name: 'BuyerAccountDetails', params: { id: buyerAccountId }, hash: '#section-traffic' }"
              target="_blank"
            >
              View in Buyer Account
            </router-link>
          </CAlert>

          <CForm>
            <div
              v-if="isLoadingSellerOffers"
              class="text-center my-3"
            >
              <CSpinner />
              <div>Loading sellers...</div>
            </div>
            <div
              v-else-if="sellersWithApprovedRegistrations.length === 0"
              class="my-3"
            >
              No sellers in this category
            </div>
            <div
              v-else
              class="my-3"
            >
              <v-multiselect-listbox
                v-model="allowedSellerIds"
                :options="sellerOptions"
                :reduce-display-property="option => option.name"
                :reduce-value-property="option => option.id"
                class="msl-multi-select--full-width"
                search-input-class="form-control"
                search-options-placeholder="Search Sellers"
                selected-options-placeholder="Search Allowed Sellers"
                selected-no-options-text="All sellers allowed"
              />
            </div>
          </CForm>

          <div class="form-row">
            <ButtonLoading
              type="submit"
              size="sm"
              color="primary"
              :loading="isLoading"
              :disabled="isLoadingAllSellersAllowed"
              @click="onSaveAllowedSellers"
            >
              Save
            </ButtonLoading>

            <ButtonLoading
              type="submit"
              size="sm"
              color="success"
              :loading="isLoadingAllSellersAllowed"
              :disabled="isLoading"
              class="ml-2"
              @click="allowAllSellers"
            >
              Allow All Sellers
            </ButtonLoading>
          </div>
        </CCol>
      </CRow>
    </CForm>
  </div>
</template>
<script lang="ts">
import Vue from 'vue'
import { mapState, mapActions, mapGetters } from 'vuex'
import { showSuccessMessage, showErrorMessage } from '@/notification-utils'
import type { AllowedSellersResponse, SellerAccount } from '@/store/campaign-traffic-control'
import { sortByName } from '@/utils'

interface ComponentData {
  isLoading: boolean
  isLoadingSellerOffers: boolean
  allowedSellerIds: string[]
  isLoadingAllSellersAllowed: boolean
}

interface ComputedProperties {
  campaignAllowedSellers: Record<string, AllowedSellersResponse>
  sellersWithApprovedRegistrations: SellerAccount[]
  allowedSellersResponseForSelectedCategory: AllowedSellersResponse | null
  allowedSellersForSelectedCategory: SellerAccount[]
  allowedSellersSource: 'buyer' | 'campaign' | null
  sellerOptions: SellerAccount[]
}

interface Methods {
  getSellerOffers: (params: { categoryId: string; includeRegistrations: boolean }) => Promise<void>
  fetchAllowedSellers: (params: { campaignId: string }) => Promise<void>
  updateAllowedSellers: (params: { campaignId: string; sellerIds: string[] }) => Promise<void>
  fetchSellerOffersForCategory: () => Promise<void>
  onSaveAllowedSellers: () => Promise<void>
  allowAllSellers: () => Promise<void>
}

export default Vue.extend<ComponentData, Methods, ComputedProperties, { campaignId: string; categoryId: string; buyerAccountId: string }>({
  name: 'BuyerCampaignTrafficControl',

  props: {
    campaignId: {
      type: String,
      required: true
    },
    categoryId: {
      type: String,
      required: true
    },
    buyerAccountId: {
      type: String,
      required: false,
      default: ''
    }
  },

  data(): ComponentData {
    return {
      isLoading: false,
      isLoadingSellerOffers: false,
      allowedSellerIds: [],
      isLoadingAllSellersAllowed: false
    }
  },

  computed: {
    ...mapState('campaignTrafficControl', [
      'campaignAllowedSellers'
    ]),

    ...mapGetters('sellerOffer', ['sellersWithApprovedRegistrations']),

    allowedSellersResponseForSelectedCategory(): AllowedSellersResponse | null {
      return this.campaignAllowedSellers[this.campaignId] || null
    },

    allowedSellersForSelectedCategory(): SellerAccount[] {
      return this.allowedSellersResponseForSelectedCategory?.allowedSellers || []
    },

    allowedSellersSource(): 'buyer' | 'campaign' | null {
      return this.allowedSellersResponseForSelectedCategory?.source?.toLowerCase() || null
    },

    sellerOptions(): SellerAccount[] {
      return [...this.sellersWithApprovedRegistrations].sort(sortByName)
    },
  },

  watch: {
    allowedSellersForSelectedCategory(newValue: SellerAccount[]) {
      this.allowedSellerIds = newValue.map(seller => seller.id)
    }
  },

  async created() {
    await this.fetchSellerOffersForCategory()
  },

  methods: {
    ...mapActions('sellerOffer', {
      getSellerOffers: 'list'
    }),

    ...mapActions('campaignTrafficControl', [
      'fetchAllowedSellers',
      'updateAllowedSellers'
    ]),

    async fetchSellerOffersForCategory(): Promise<void> {
      this.isLoadingSellerOffers = true

      try {
        await this.getSellerOffers({
          categoryId: this.categoryId,
          includeRegistrations: true
        })

        await this.fetchAllowedSellers({
          campaignId: this.campaignId
        })
      } catch (error) {
        showErrorMessage('Failed to load sellers')
      } finally {
        this.isLoadingSellerOffers = false
      }
    },

    async onSaveAllowedSellers(): Promise<void> {
      this.isLoading = true

      try {
        await this.updateAllowedSellers({
          campaignId: this.campaignId,
          sellerIds: this.allowedSellerIds
        })

        showSuccessMessage('Allowed sellers for this campaign saved successfully')
      } catch (error) {
        showErrorMessage('Failed to save allowed sellers')
      } finally {
        this.isLoading = false
      }
    },

    async allowAllSellers(): Promise<void> {
      this.isLoadingAllSellersAllowed = true
      this.allowedSellerIds = []

      try {
        await this.updateAllowedSellers({
          campaignId: this.campaignId,
          sellerIds: this.allowedSellerIds
        })

        showSuccessMessage('All sellers have been allowed for this campaign')
      } catch (error) {
        showErrorMessage('Failed to allow all sellers')
      } finally {
        this.isLoadingAllSellersAllowed = false
      }
    }
  }
})
</script>
<style scoped>
  
</style>
